import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RISK_LABEL,
  OUTCOME_LABEL,
  GROUP_NAMES,
  formatAmericanOdds,
  formatProbability,
  formatDate,
} from 'utils';
import type { ApiPick, PicksFilterValues } from 'services/types';
import LockPickTM from 'components/LockPickTM/LockPickTM';
import Logo from 'components/Logo/Logo';
import PickLeg from 'components/PickLeg/PickLeg';
import { setOurBet, setAsFavorite } from 'services/picksApi';

import './PickCard.scss';

type Props = {
  pick: ApiPick;
  userGroups?: string[];
  isInsightsPage?: boolean;
  currentFilters?: PicksFilterValues;
  resetAndLoad?: () => void;
};

function PickCard({ pick, userGroups, isInsightsPage, currentFilters, resetAndLoad }: Props) {
  const navigate = useNavigate();
  const computedDateString = pick?.computedDate ? formatDate(pick.computedDate) : '';
  const isAdmin = userGroups?.includes(GROUP_NAMES.admin);

  const handleSetOurBet = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.stopPropagation();
    setOurBet(pick.pickId, pick.ourBet ? false : true);
    if (resetAndLoad) {
      resetAndLoad();
    }
  };

  const handleSetFavorite = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    e.stopPropagation();
    setAsFavorite(pick.pickId, pick.favorite ? false : true);
    if (resetAndLoad) {
      resetAndLoad();
    }
  };

  return (
    <div
      className={`pickCard pickCard--${pick.riskLevel}`}
      onClick={() => {
        if (!isInsightsPage) {
          navigate(`/picks/${pick.pickId}`, {
            state: { currentFilters: currentFilters ?? null },
          });
        }
      }}
    >
      <div className={'pickCard__header'}>
        <div className={'pickCard__header-left'}>
          <span className={`pickCard__risk pickCard__risk--${pick.riskLevel}`}>
            {pick.riskLevel === 'lock' ? <LockPickTM /> : RISK_LABEL[pick.riskLevel]}
          </span>
          <span className={'pickCard__event'}>{pick.eventName ?? pick.eventId}</span>
        </div>
        <div className={'pickCard__header-right'}>
          <div className={'starRating'}>
            <input
              type="radio"
              className={`pickCard__favorite-badge ${pick.favorite ? 'active' : ''}`}
              onClick={handleSetFavorite}
              value={pick.pickId}
            />
          </div>
          <span className={'pickCard__odds'}>{formatAmericanOdds(pick.parlayAmericanOdds)}</span>
          <span
            className={`pickCard__outcome pickCard__outcome--${pick.outcome}`}
            title={`Parlay outcome: ${OUTCOME_LABEL[pick.outcome]}`}
          >
            {OUTCOME_LABEL[pick.outcome]}
          </span>
        </div>
      </div>
      <div className={'pickCard__legs'}>
        {pick.legs.map((leg) => (
          <PickLeg key={leg.legIndex} leg={leg} pick={pick} />
        ))}
      </div>
      <div className={'pickCard__footer'}>
        <div className={'pickCard__prob'}>
          <Logo size="small" />
          <span className={'pickCard__prob-value'}>
            EQ Odds {formatProbability(pick.parlayProbHigh)}
          </span>
        </div>
        <span className={'pickCard__date'}>
          {isAdmin && (
            <span className={'pickCard__ourBet'}>
              <a
                href={'javascript:void(0)'}
                onClick={handleSetOurBet}
                className={'pickCard__ourBet-link'}
              >
                {`${pick.ourBet ? 'Clear Our Bet' : 'Set Our Bet'}`}
              </a>
            </span>
          )}
          Pick Last Updated: {computedDateString}
        </span>
      </div>
    </div>
  );
}

export default PickCard;
